import { useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import FloatingCTA from "@/components/feature/FloatingCTA";
import SEO from "@/components/feature/SEO";
import { webpageSchema, breadcrumbSchema, siteUrl } from "@/lib/schemas";
import { User, Heart, Briefcase, Home, Users, IndianRupee, Download, ArrowRight } from "lucide-react";

export default function TermCalculatorPage() {
  const baseUrl = siteUrl;
  const pageUrl = `${baseUrl}/calculator/term`;

  const schemas = [
    webpageSchema(
      "Term Insurance Calculator | Coverage & Premium Estimator | ExpertAdvise.in",
      "Calculate ideal term insurance coverage and estimated premiums based on income, loans, dependents, and lifestyle. Compare top term plans.",
      pageUrl
    ),
    breadcrumbSchema([
      { name: "Home", url: baseUrl },
      { name: "Calculators", url: `${baseUrl}/calculators` },
      { name: "Term Insurance Calculator", url: pageUrl },
    ]),
  ];

  const [annualIncome, setAnnualIncome] = useState(12);
  const [age, setAge] = useState(30);
  const [hasLoans, setHasLoans] = useState(true);
  const [loanAmount, setLoanAmount] = useState(50);
  const [dependents, setDependents] = useState(3);
  const [lifestyle, setLifestyle] = useState("moderate");

  const coverageNeeded = useMemo(() => {
    const incomeMultiplier = lifestyle === "conservative" ? 15 : lifestyle === "moderate" ? 20 : 25;
    const incomeCover = annualIncome * incomeMultiplier;
    const loanCover = hasLoans ? loanAmount : 0;
    const dependentCover = dependents * 15;
    return Math.round(incomeCover + loanCover + dependentCover);
  }, [annualIncome, lifestyle, hasLoans, loanAmount, dependents]);

  const estimatedPremium = useMemo(() => {
    const baseRate = age <= 30 ? 0.0009 : age <= 40 ? 0.0015 : age <= 50 ? 0.003 : 0.006;
    return Math.round(coverageNeeded * baseRate * 12);
  }, [coverageNeeded, age]);

  const formatCurrency = useCallback((val: number) => {
    if (val >= 10000000) return `₹${(val / 10000000).toFixed(2)} Cr`;
    if (val >= 100000) return `₹${(val / 100000).toFixed(0)} Lakhs`;
    return `₹${val.toLocaleString()}`;
  }, []);

  const handleDownload = () => {
    alert("PDF report generation will be enabled after backend integration.");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO
        title="Term Insurance Calculator | Coverage & Premium Estimator | ExpertAdvise.in"
        description="Calculate ideal term insurance coverage and estimated premiums based on income, loans, dependents, and lifestyle. Compare top term plans."
        canonical={pageUrl}
        ogUrl={pageUrl}
        schemas={schemas}
      />
      <Navbar />

      <section className="pt-24 md:pt-32 pb-10 md:pb-14 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-block bg-blue-50 text-blue-700 text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full mb-3">
              Insurance Calculator
            </span>
            <h1 className="text-2xl md:text-4xl font-bold text-slate-900 mb-4">
              Term Insurance Calculator
            </h1>
            <p className="text-slate-600 text-base md:text-lg leading-relaxed">
              Calculate how much term insurance coverage you need based on your income, loans, dependents, and lifestyle. 
              Get instant premium estimates from leading insurers.
            </p>
          </div>
        </div>
      </section>

      <section className="py-10 md:py-14">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
            {/* Inputs */}
            <div className="lg:col-span-1 space-y-6">
              <div className="bg-white rounded-2xl border border-slate-200 p-5 md:p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-5">Your Profile</h3>

                <div className="space-y-5">
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
                      <IndianRupee className="w-4 h-4 text-primary-500" />
                      Annual Income (₹ Lakhs)
                    </label>
                    <input
                      type="range"
                      min="3"
                      max="100"
                      step="1"
                      value={annualIncome}
                      onChange={(e) => setAnnualIncome(Number(e.target.value))}
                      className="w-full accent-primary-600 mb-2"
                    />
                    <div className="flex items-center justify-between text-sm text-slate-500">
                      <span>₹3L</span>
                      <span className="font-semibold text-primary-700">₹{annualIncome}L</span>
                      <span>₹100L</span>
                    </div>
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
                      <User className="w-4 h-4 text-primary-500" />
                      Current Age
                    </label>
                    <input
                      type="range"
                      min="18"
                      max="65"
                      step="1"
                      value={age}
                      onChange={(e) => setAge(Number(e.target.value))}
                      className="w-full accent-primary-600 mb-2"
                    />
                    <div className="flex items-center justify-between text-sm text-slate-500">
                      <span>18</span>
                      <span className="font-semibold text-primary-700">{age} Years</span>
                      <span>65</span>
                    </div>
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
                      <Users className="w-4 h-4 text-primary-500" />
                      Number of Dependents
                    </label>
                    <div className="flex gap-2">
                      {[1, 2, 3, 4, 5].map((num) => (
                        <button
                          key={num}
                          onClick={() => setDependents(num)}
                          className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                            dependents === num
                              ? "bg-primary-600 text-white"
                              : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                          }`}
                        >
                          {num === 5 ? "5+" : num}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
                      <Home className="w-4 h-4 text-primary-500" />
                      Outstanding Loans?
                    </label>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setHasLoans(true)}
                        className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                          hasLoans
                            ? "bg-primary-600 text-white"
                            : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                        }`}
                      >
                        Yes
                      </button>
                      <button
                        onClick={() => setHasLoans(false)}
                        className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                          !hasLoans
                            ? "bg-primary-600 text-white"
                            : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                        }`}
                      >
                        No
                      </button>
                    </div>
                  </div>

                  {hasLoans && (
                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
                        <Briefcase className="w-4 h-4 text-primary-500" />
                        Total Loan Amount (₹ Lakhs)
                      </label>
                      <input
                        type="range"
                        min="5"
                        max="200"
                        step="5"
                        value={loanAmount}
                        onChange={(e) => setLoanAmount(Number(e.target.value))}
                        className="w-full accent-primary-600 mb-2"
                      />
                      <div className="flex items-center justify-between text-sm text-slate-500">
                        <span>₹5L</span>
                        <span className="font-semibold text-primary-700">₹{loanAmount}L</span>
                        <span>₹200L</span>
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
                      <Heart className="w-4 h-4 text-primary-500" />
                      Lifestyle Standard
                    </label>
                    <select
                      value={lifestyle}
                      onChange={(e) => setLifestyle(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-primary-400"
                    >
                      <option value="conservative">Conservative (15x Income)</option>
                      <option value="moderate">Moderate (20x Income)</option>
                      <option value="premium">Premium (25x Income)</option>
                    </select>
                  </div>
                </div>
              </div>

              <button
                onClick={handleDownload}
                className="w-full flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-semibold px-5 py-3 rounded-xl transition-colors"
              >
                <Download className="w-4 h-4" />
                Download PDF Report
              </button>
            </div>

            {/* Results */}
            <div className="lg:col-span-2 space-y-6">
              {/* Coverage Card */}
              <div className="bg-gradient-to-br from-primary-600 to-primary-800 rounded-2xl p-6 md:p-8 text-white">
                <div className="text-sm text-white/80 mb-2">Recommended Coverage</div>
                <div className="text-4xl md:text-5xl font-bold mb-2">{formatCurrency(coverageNeeded * 100000)}</div>
                <div className="text-sm text-white/70 mb-6">
                  Based on {lifestyle} lifestyle ({lifestyle === "conservative" ? "15x" : lifestyle === "moderate" ? "20x" : "25x"} income + loans + dependents)
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-6 border-t border-white/20">
                  <div>
                    <div className="text-xs text-white/60 mb-1">Income Cover</div>
                    <div className="font-semibold">{formatCurrency(annualIncome * (lifestyle === "conservative" ? 15 : lifestyle === "moderate" ? 20 : 25) * 100000)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-white/60 mb-1">Loan Cover</div>
                    <div className="font-semibold">{hasLoans ? formatCurrency(loanAmount * 100000) : "₹0"}</div>
                  </div>
                  <div>
                    <div className="text-xs text-white/60 mb-1">Dependent Cover</div>
                    <div className="font-semibold">{formatCurrency(dependents * 15 * 100000)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-white/60 mb-1">Est. Premium</div>
                    <div className="font-semibold">₹{estimatedPremium.toLocaleString()}/yr</div>
                  </div>
                </div>
              </div>

              {/* Recommendations */}
              <div className="bg-white rounded-2xl border border-slate-200 p-5 md:p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4">Recommended Term Plans</h3>
                <div className="space-y-3">
                  {[
                    { name: "HDFC Life Click 2 Protect", premium: Math.round(estimatedPremium * 0.92), term: 30, claimRatio: "99.5%" },
                    { name: "ICICI Pru iProtect Smart", premium: Math.round(estimatedPremium * 0.98), term: 30, claimRatio: "97.8%" },
                    { name: "Max Life Smart Secure Plus", premium: Math.round(estimatedPremium * 1.05), term: 30, claimRatio: "99.3%" },
                  ].map((plan, i) => (
                    <div key={i} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                      <div>
                        <div className="font-semibold text-slate-900">{plan.name}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{plan.term}-year term • Claim ratio: {plan.claimRatio}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-slate-900">₹{plan.premium.toLocaleString()}/yr</div>
                      </div>
                    </div>
                  ))}
                </div>
                <Link
                  to="/term-insurance"
                  className="mt-4 flex items-center justify-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700"
                >
                  View all term insurance plans
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

              {/* Why Term Insurance */}
              <div className="bg-white rounded-2xl border border-slate-200 p-5 md:p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4">Why You Need Term Insurance</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { icon: Users, title: "Protect Family", desc: "Replace your income for dependents if something happens to you" },
                    { icon: Home, title: "Cover Loans", desc: "Ensure home loans, car loans, and education debts are paid off" },
                    { icon: Heart, title: "Peace of Mind", desc: "Sleep peacefully knowing your loved ones are financially secure" },
                    { icon: Briefcase, title: "Low Premium", desc: "Term plans offer the highest coverage at the lowest cost" },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3 p-3">
                      <div className="w-9 h-9 rounded-lg bg-primary-50 flex items-center justify-center shrink-0">
                        <item.icon className="w-4.5 h-4.5 text-primary-600" />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-slate-900">{item.title}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{item.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-primary-50">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-3">
            Get the Best Term Insurance Deal
          </h2>
          <p className="text-slate-600 mb-6">
            Our advisors compare 20+ term plans and negotiate the best premium for your profile.
          </p>
          <Link
            to="/book-appointment?source=Term%20Calculator%20Page"
            className="inline-block bg-cta-600 hover:bg-cta-700 text-white font-semibold px-6 py-3 rounded-full transition-colors whitespace-nowrap"
          >
            Compare & Buy Now
          </Link>
        </div>
      </section>

      <Footer />
      <FloatingCTA />
    </div>
  );
}